import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Form, Input, Select, Switch, Button, Card, message, Spin, Space } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import {
  getIntegrationConfig,
  createIntegrationConfig,
  updateIntegrationConfig,
  PROVIDER_TYPES,
} from '@/api/integrationApi';
import IntegrationNav from './IntegrationNav';

export default function IntegrationConfigForm() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const isEdit = !!id;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [providerType, setProviderType] = useState<string>('google');

  const loadData = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const data = await getIntegrationConfig(id);
      setProviderType(data.provider_type);
      form.setFieldsValue({
        provider_type: data.provider_type,
        provider_name: data.provider_name,
        client_id: data.client_id,
        client_secret: '', // never populate masked value
        scopes: data.scopes?.join(', '),
        redirect_uri: data.redirect_uri,
        auth_endpoint: data.auth_endpoint,
        token_endpoint: data.token_endpoint,
        is_active: data.is_active,
        config_data: '', // never populate masked value
        description: data.description,
      });
    } catch {
      message.error(t('app.error'));
    } finally {
      setLoading(false);
    }
  }, [id, form, t]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSubmit = async (values: Record<string, unknown>) => {
    setSaving(true);
    try {
      const scopesStr = (values.scopes as string) || '';
      const scopes = scopesStr.split(',').map((s: string) => s.trim()).filter(Boolean);

      if (isEdit) {
        const update: Record<string, unknown> = {
          provider_name: values.provider_name,
          client_id: values.client_id,
          scopes,
          redirect_uri: values.redirect_uri,
          auth_endpoint: values.auth_endpoint || undefined,
          token_endpoint: values.token_endpoint || undefined,
          is_active: values.is_active,
          description: values.description,
        };
        // Only send secrets/config_data if user typed new values
        if (values.client_secret) update.client_secret = values.client_secret;
        if (values.config_data) update.config_data = values.config_data;
        await updateIntegrationConfig(id!, update);
      } else {
        await createIntegrationConfig({
          provider_type: values.provider_type as string,
          provider_name: values.provider_name as string,
          client_id: values.client_id as string,
          client_secret: values.client_secret as string,
          scopes,
          redirect_uri: values.redirect_uri as string,
          auth_endpoint: (values.auth_endpoint as string) || undefined,
          token_endpoint: (values.token_endpoint as string) || undefined,
          is_active: values.is_active as boolean,
          config_data: (values.config_data as string) || '',
          description: (values.description as string) || '',
        });
      }
      message.success(t('integration.config_saved'));
      navigate('/integration/configs');
    } catch {
      message.error(t('app.error'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />;

  return (
    <div>
      <IntegrationNav />
      <Button
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate('/integration/configs')}
        style={{ marginBottom: 16 }}
      >
        {t('app.back')}
      </Button>

      <Card title={isEdit ? t('integration.edit_config_title') : t('integration.create_config_title')}>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            is_active: true,
            provider_type: 'google',
          }}
        >
          <Form.Item
            name="provider_type"
            label={t('integration.field.provider_type')}
            rules={[{ required: true }]}
          >
            <Select
              disabled={isEdit}
              options={PROVIDER_TYPES.map((pt) => ({
                value: pt,
                label: t(`integration.provider_type.${pt}`),
              }))}
              onChange={(v) => setProviderType(v)}
            />
          </Form.Item>

          <Form.Item
            name="provider_name"
            label={t('integration.field.provider_name')}
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>

          <Space style={{ width: '100%' }} direction="vertical">
            <Form.Item
              name="client_id"
              label={t('integration.field.client_id')}
              rules={[{ required: true }]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              name="client_secret"
              label={t('integration.field.client_secret')}
              rules={isEdit ? [] : [{ required: true }]}
              extra={isEdit ? t('integration.secret_edit_hint') : undefined}
            >
              <Input.Password />
            </Form.Item>
          </Space>

          <Form.Item
            name="scopes"
            label={t('integration.field.scopes')}
            extra={t('integration.scopes_hint')}
          >
            <Input placeholder="openid, email, profile" />
          </Form.Item>

          <Form.Item
            name="redirect_uri"
            label={t('integration.field.redirect_uri')}
            rules={[{ required: true }, { type: 'url' }]}
          >
            <Input placeholder="https://example.com/oauth/callback" />
          </Form.Item>

          {providerType === 'custom' && (
            <>
              <Form.Item
                name="auth_endpoint"
                label={t('integration.field.auth_endpoint')}
                rules={[{ required: true }, { type: 'url' }]}
              >
                <Input placeholder="https://provider.com/oauth/authorize" />
              </Form.Item>

              <Form.Item
                name="token_endpoint"
                label={t('integration.field.token_endpoint')}
                rules={[{ required: true }, { type: 'url' }]}
              >
                <Input placeholder="https://provider.com/oauth/token" />
              </Form.Item>
            </>
          )}

          <Form.Item
            name="is_active"
            label={t('integration.field.is_active')}
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>

          <Form.Item
            name="config_data"
            label={t('integration.field.config_data')}
            extra={isEdit ? t('integration.config_data_edit_hint') : t('integration.config_data_hint')}
          >
            <Input.TextArea rows={4} />
          </Form.Item>

          <Form.Item
            name="description"
            label={t('integration.field.description')}
          >
            <Input.TextArea rows={2} />
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" loading={saving}>
              {t('app.save')}
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
}
